import React, { Suspense, useCallback, useMemo, useState } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { calMaxWidth, ResizeContainerV2, useCalculatedWidth } from '@lyearn/lygo-blocks';
import { cn } from '@lyearn/style';
import { Autocomplete } from '@lyearn/ui';
import Loader from '@/components/Loader';
import useDebounceState from '@/hooks/useDebounceState';
import { RenderInput } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/Search/renderers';
import { ScormViewerMode } from '@/modules/corporate/modules/scorm/views/ScormViewer';
import { OrderEnum, ResourceStatusEnum, ResourceTypeEnum, ScormContentType } from '@/types/schema';
import dynamic from '@/utils/dynamic';

import { useScormContentsQuery } from '../../../scorm/graphql/queries/generated/scormContents';
import { useCourse } from '../../courseContext';
import CourseEntity from '../../entity/Course';
import { renderOption } from './renderers';
import { FOOTER_HEIGHT, rndClassname, SCORM_SIDE_SPACING } from './styles';
import { ScormCardContainerProps, ScormOption } from './types';

const ScormContainerLoadale = dynamic(
  () =>
    import(
      /* webpackChunkName: "scorm-container" */ '@/modules/corporate/modules/scorm/views/ScormViewer/ScormEmbed'
    ),
);

const initialPos = {
  x: 0,
  y: 0,
  /* Scorm embed default height/width as in design */
  width: 758,
  height: 427 + FOOTER_HEIGHT,
};

const minSize = { width: 576, height: 372 };

export const SCORM_ASPECT_RATIO = initialPos.height / initialPos.width;

const getOptionLabel = (option: ScormOption) => option.title;

const ScormCard: React.FC<ScormCardContainerProps> = (props) => {
  const { t } = useTranslation();
  const { updateAttribute, scormId, editable, duration, id, width, height } = props;
  const [inputValue, setValue, searchString] = useDebounceState('');
  const [scormFullscreen, setScormFullscreen] = useState(false);

  const { course } = useCourse();

  const [{ data }] = useScormContentsQuery({
    variables: {
      filters: {
        name: searchString,
        types: [ResourceTypeEnum.Scorm],
        shareable: course.isSent ? true : undefined,
        status: [ResourceStatusEnum.Published, ResourceStatusEnum.UnpublishedChanges],
      },
      sort: [{ field: 'relevance', order: OrderEnum.Desc }],
    },
  });

  const options: ScormOption[] = useMemo(() => {
    if (data?.contents.edges?.length) {
      return data.contents.edges.map(({ node }) => {
        const { id, name, cover } = node as ScormContentType;

        return {
          id,
          title: name,
          image: cover?.url,
        };
      }, []);
    }

    return [];
  }, [data?.contents.edges]);

  const onChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: ScormOption) => {
      updateAttribute({ scormId: value.id });
    },
    [updateAttribute],
  );

  const onInputChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: string) => {
      setValue(value);
    },
    [setValue],
  );

  const onLoad = useCallback(
    (scorm: any) => {
      if (editable && scorm?.duration && scorm.duration !== duration) {
        updateAttribute({ duration: scorm.duration });
      }
    },
    [duration, editable, updateAttribute],
  );

  const _renderInput = useCallback(
    (params: any) =>
      RenderInput({
        params,
        onBlurPlaceholder: t('Course:Builder.AttachScorm'),
        onFocusPlaceholder: t('Course:Builder.SearchForScorm'),
      }),
    [t],
  );

  const courseId = CourseEntity.id(course);

  const calculatedWidth = useCalculatedWidth(width);
  initialPos.width = calculatedWidth ?? initialPos.width;
  initialPos.height = height ? height : SCORM_ASPECT_RATIO * calculatedWidth;

  if (!courseId) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative my-32 flex items-center',
        { 'pointer-events-none': !courseId },
        { 'pb-36': scormId },
      )}>
      {scormId ? (
        <Suspense fallback={<Loader />}>
          <ResizeContainerV2
            className={rndClassname(scormFullscreen)}
            defaultSize={initialPos}
            lockAspectRatio={false}
            maxWidth={calMaxWidth() - SCORM_SIDE_SPACING}
            minHeight={minSize.height}
            minWidth={minSize.width}>
            <ScormContainerLoadale
              contentId={scormId}
              editable={true}
              floatingFooter={true}
              languageType={course.languageType}
              mode={ScormViewerMode.Embed}
              parentContentId={courseId}
              onFullScreenChange={setScormFullscreen}
              onLoad={onLoad}
            />
          </ResizeContainerV2>
        </Suspense>
      ) : editable ? (
        <Autocomplete
          disableClearable
          disablePortal
          className="w-full"
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          optionLabelRenderer={renderOption}
          options={options}
          popupIcon={null}
          renderInput={_renderInput}
          size="large"
          onChange={onChange}
          onInputChange={onInputChange}
          onKeyDown={stopEventPropagation}
          onKeyPress={stopEventPropagation}
          onPaste={stopEventPropagation}
        />
      ) : (
        <div />
      )}
    </div>
  );
};

export default ScormCard;
